import React from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { BusinessForm } from 'api/common/constants'
import { ConnectorPreRegisterRequest } from 'api/common/requests'
import { Status } from 'api/common/statuses'
import { connectorPreRegisterAPI } from 'api/connector'
import Footer from 'components/Footer'
import Header from 'components/Header'
import InputFieldConfirm from 'components/InputFieldConfirm'
import { preRegisterFormItems } from 'utilities/resources/formItems'
import { PreRegisterFormValues } from 'utilities/resources/formItemValues'
import { getBusinessFormLabel } from 'utilities/resources/options'
import { autoCastOrUndefined, birthdayFormatter } from 'utilities/typeUtility'
import { checkInviteCode, isSelectedSummaryDocument } from 'utilities/validators'
import { ROUTE_REGISTRATION_EXPIRED } from 'constants/routes'

type StateValue = {
  formValues?: PreRegisterFormValues
}

/** STATUS_TO_MESSAGE に該当しないステータス（通信エラー 'failed' 含む）向けの汎用エラー文言 */
const FALLBACK_REGISTER_ERROR_MESSAGE = '登録処理でエラーが発生しました。時間をおいて再度お試しください。' as const

/**
 * 仮登録APIのエラーステータス → 画面表示メッセージ（CampaignSignUpConfirm と同パターン）。
 * 画面遷移を伴う Success / CantPositionAssignment はここに含めず callback 内で分岐する。
 */
const STATUS_TO_MESSAGE: Partial<Record<Status, string>> = {
  [Status.AlreadyRegistered]: 'この個人情報は既に登録されています。',
  [Status.InvalidPositionNumber]: 'ご入力いただいたお名前でご登録できません。\n事務局までご連絡ください。',
  [Status.NotFoundInvitePosition]: '招待URLが無効です。QRコードを再度読み取り直すか、ご案内のURLに誤りがないかご確認ください。',
  [Status.ValidateError]: '入力内容に誤りがあります。「登録内容を修正する」から入力内容をご確認ください。',
}

const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { type, hash } = useParams()
  const stateValue = React.useMemo(() => (location.state ?? {}) as StateValue, [location])
  const { formValues } = stateValue

  const [message, setMessage] = React.useState('')
  const [committed, setCommitted] = React.useState(false)

  /**
   * 「メールアドレス確認」ボタン
   */
  const handleCheckMailAddressButton = async () => {
    setCommitted(true)
    const requestParam: ConnectorPreRegisterRequest = {
      email: formValues?.email,
      userType: autoCastOrUndefined(formValues?.businessForm),
      companyName: formValues?.companyName,
      firstName: formValues?.firstName,
      lastName: formValues?.lastName,
      birthday: formValues?.birthday,
      password: formValues?.password,
      registerType: autoCastOrUndefined(type),
      hash: hash,
      summaryDocumentKind: formValues?.summaryDocumentKind,
      documentNumbers: formValues?.summaryDocumentNumbers,
    }
    connectorPreRegisterAPI(requestParam, (status) => {
      setCommitted(false)
      if (status === Status.Success) {
        navigate('/signUpPreRegisterConnectorComplete', { state: { email: formValues?.email } })
        return
      }
      if (status === Status.CantPositionAssignment) {
        navigate(ROUTE_REGISTRATION_EXPIRED)
        return
      }
      setMessage(STATUS_TO_MESSAGE[status] ?? FALLBACK_REGISTER_ERROR_MESSAGE)
    })
  }

  React.useEffect(() => {
    if (!checkInviteCode(type, hash) || !isSelectedSummaryDocument(formValues)) {
      navigate('/')
    } else if (!formValues.email) {
      navigate(`/signUpPreRegisterConnector/${type}/${hash}`, { state: { formValues } })
    }
  })
  return (
    <>
      <Header title="コネクター仮登録" />
      <main>
        <section className="mx-4 my-5">
          <p>ご入力内容をご確認ください。確認後はページ下のボタンを押し、入力フォームを送信してください。</p>
          <InputFieldConfirm label={preRegisterFormItems.email.label} value={formValues?.email} bold />
          <InputFieldConfirm label={preRegisterFormItems.businessForm.label} value={getBusinessFormLabel(formValues?.businessForm ?? '')} bold />
          {formValues?.businessForm === `${BusinessForm.Company}` && <InputFieldConfirm label={preRegisterFormItems.companyName.label} value={formValues.companyName} bold />}
          <InputFieldConfirm label={preRegisterFormItems.lastName.label} value={formValues?.lastName} bold />
          <InputFieldConfirm label={preRegisterFormItems.firstName.label} value={formValues?.firstName} bold />
          <InputFieldConfirm label={preRegisterFormItems.birthday.label} value={birthdayFormatter(formValues?.birthday)} bold />
          <InputFieldConfirm label={preRegisterFormItems.password.label} value="●●●●●●●●●●●●" bold />
          {message && <p className="note text-danger" style={{ whiteSpace: 'pre-wrap' }}>{message}</p>}
          <div className="mt-5">
            <Button variant="primary" className="w-100 py-4" disabled={committed} onClick={handleCheckMailAddressButton}>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" hidden={!committed} />
              メールアドレス確認
            </Button>
          </div>
          <div className="mt-5 text-center">
            <Button variant="link" onClick={() => navigate(`/signUpPreRegisterConnector/${type}/${hash}`, { state: { formValues } })}>
              登録内容を修正する
            </Button>
          </div>
        </section>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent
